import { useState, useCallback } from 'react';
import { useIntl } from 'react-intl';
import { Text, Button, Badge, Tab, TabList, Menu, MenuTrigger, MenuPopover, MenuList, MenuItem } from '@fluentui/react-components';
import { DocumentRegular, ArrowLeftRegular, ArrowDownloadRegular, CheckmarkCircleRegular, EditRegular, EyeRegular, ArrowSyncRegular, ErrorCircleRegular } from '@fluentui/react-icons';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import '../components/MarkdownMessage.css';
import type { MethodologyDocument } from '../services/document-service';
import { updateDocumentStatus } from '../services/document-service';
import { apiUrl } from '../utils/api-url';
import { useApiToken } from '../hooks/useApiToken';
import { getBadgeProps, DOC_STATUS_BADGE, getCompletenessColor } from '../utils/badge-config';

const STATUS_ICONS: Record<string, typeof EditRegular> = {
  draft: EditRegular,
  review: EyeRegular,
  final: CheckmarkCircleRegular,
};

const NEXT_STATUS: Record<string, string> = {
  draft: 'review',
  review: 'final',
};

interface DocumentViewerPageProps {
  document: MethodologyDocument;
  onBack: () => void;
  onDownloadPdf?: () => void;
  onStatusChange?: (docId: string, newStatus: string) => void;
}

export function DocumentViewerPage({ document, onBack, onDownloadPdf, onStatusChange }: DocumentViewerPageProps) {
  const intl = useIntl();
  const getToken = useApiToken();
  const [activeTab, setActiveTab] = useState('rendered');
  const [currentStatus, setCurrentStatus] = useState(document.status);
  const [updating, setUpdating] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [completeness, setCompleteness] = useState<number | null>(null);
  const [content, setContent] = useState(document.markdownContent);
  const [error, setError] = useState<string | null>(null);

  const handleRegenerate = useCallback(async () => {
    setError(null);
    setRegenerating(true);
    try {
      const token = await getToken();
      const res = await fetch(apiUrl(`/api/documents/${document.id}/regenerate?customerId=${encodeURIComponent(document.customerId)}`), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Regenerate failed: ${res.status}`);
      const result = await res.json();
      setCompleteness(result.completeness);
      // Reload the document content
      const docRes = await fetch(apiUrl(`/api/documents/${document.id}?customerId=${encodeURIComponent(document.customerId)}`), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (docRes.ok) {
        const updatedDoc = await docRes.json();
        setContent(updatedDoc.markdownContent);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Regenerate failed.');
    } finally {
      setRegenerating(false);
    }
  }, [document.id, document.customerId, getToken]);

  const handleStatusChange = async (newStatus: string, force = false) => {
    setUpdating(true);
    try {
      const token = await getToken();
      await updateDocumentStatus(document.id!, document.customerId, newStatus, token, force);
      setCurrentStatus(newStatus);
      onStatusChange?.(document.id!, newStatus);
    } catch (err) {
      console.error('Status update failed:', err);
    } finally {
      setUpdating(false);
    }
  };

  const nextStatus = NEXT_STATUS[currentStatus];
  const StatusIcon = STATUS_ICONS[currentStatus] ?? EditRegular;

  return (
    <div className="page-root page-root--scrollable document-viewer-page">
      <div className="page-title-bar">
        <Button appearance="subtle" icon={<ArrowLeftRegular />} onClick={onBack} title={intl.formatMessage({ id: 'documentViewer.backButton.title' })} />
        <DocumentRegular className="icon-title text-brand" />
        <Text size={500} weight="semibold">{document.title}</Text>
        <div className="title-bar-actions">
          {/* Status workflow controls */}
          <Menu>
            <MenuTrigger disableButtonEnhancement>
              <Button
                appearance="outline"
                size="small"
                icon={<StatusIcon />}
                disabled={updating}
              >
                <Badge size="small" {...getBadgeProps(DOC_STATUS_BADGE, currentStatus)}>{currentStatus}</Badge>
              </Button>
            </MenuTrigger>
            <MenuPopover>
              <MenuList>
                {nextStatus && (
                  <MenuItem icon={<CheckmarkCircleRegular />} onClick={() => handleStatusChange(nextStatus)}>
                    {intl.formatMessage({ id: 'documentViewer.status.promoteTo' }, { status: nextStatus })}
                  </MenuItem>
                )}
                {currentStatus === 'final' && (
                  <MenuItem icon={<EditRegular />} onClick={() => handleStatusChange('draft', true)}>
                    {intl.formatMessage({ id: 'documentViewer.status.revertToDraft' })}
                  </MenuItem>
                )}
                {currentStatus === 'review' && (
                  <MenuItem icon={<EditRegular />} onClick={() => handleStatusChange('draft', true)}>
                    {intl.formatMessage({ id: 'documentViewer.status.backToDraft' })}
                  </MenuItem>
                )}
              </MenuList>
            </MenuPopover>
          </Menu>

          <Button
            appearance="subtle"
            icon={<ArrowSyncRegular />}
            onClick={handleRegenerate}
            disabled={regenerating}
            size="small"
            title={intl.formatMessage({ id: 'documentViewer.regenerateButton.title' })}
          >
            {regenerating
              ? intl.formatMessage({ id: 'documentViewer.regenerateButton.regenerating' })
              : intl.formatMessage({ id: 'documentViewer.regenerateButton.idle' })}
          </Button>
          {completeness !== null && (
            <Badge size="small" color={getCompletenessColor(completeness)}>
              {intl.formatMessage({ id: 'documentViewer.completeness' }, { pct: Math.round(completeness * 100) })}
            </Badge>
          )}
          <Badge size="small" appearance="outline">{document.cafPhase}</Badge>
          <Badge size="small" appearance="outline">v{document.version}</Badge>
          {onDownloadPdf && (
            <Button appearance="subtle" icon={<ArrowDownloadRegular />} onClick={onDownloadPdf} title={intl.formatMessage({ id: 'documentViewer.downloadPdf.title' })} />
          )}
        </div>
      </div>

      {error && (
        <div className="page-error">
          <ErrorCircleRegular />
          <Text className="text-danger">{error}</Text>
          <Button appearance="primary" onClick={handleRegenerate}>{intl.formatMessage({ id: 'documentViewer.retryButton' })}</Button>
        </div>
      )}

      <div className="viewer-tabs">
        <TabList selectedValue={activeTab} onTabSelect={(_e, d) => setActiveTab(d.value as string)}>
          <Tab value="rendered">{intl.formatMessage({ id: 'documentViewer.tab.rendered' })}</Tab>
          <Tab value="markdown">{intl.formatMessage({ id: 'documentViewer.tab.markdown' })}</Tab>
        </TabList>
      </div>

      <div className="viewer-content">
        {activeTab === 'rendered' && (
          <div className="markdown-body">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                img: ({ ...props }) => (
                  <img {...props} className="markdown-body-img" />
                ),
              }}
            >
              {content}
            </ReactMarkdown>
          </div>
        )}
        {activeTab === 'markdown' && (
          <pre className="markdown-source">{document.markdownContent}</pre>
        )}
      </div>
    </div>
  );
}
